import { importEntities } from "soapbox/entity-store/actions.ts";
import { Entities } from "soapbox/entity-store/entities.ts";
import { useTransaction } from "soapbox/entity-store/hooks/index.ts";
import { useApi } from "soapbox/hooks/useApi.ts";
import { useAppDispatch } from "soapbox/hooks/useAppDispatch.ts";
import { useLoggedIn } from "soapbox/hooks/useLoggedIn.ts";
import { relationshipSchema, accountSchema } from "soapbox/schemas/index.ts";
import { isFollowDisabled } from "soapbox/utils/accounts.ts";

interface FollowOpts {
  reblogs?: boolean;
  notify?: boolean;
  languages?: string[];
}

function useFollow() {
  const api = useApi();
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useLoggedIn();
  const { transaction } = useTransaction();

  function followEffect(accountId: string) {
    transaction({
      Accounts: {
        [accountId]: (account) => ({
          ...account,
          followers_count: account.followers_count + 1,
        }),
      },
      Relationships: {
        [accountId]: (relationship) => ({
          ...relationship,
          following: true,
        }),
      },
    });
  }

  function unfollowEffect(accountId: string) {
    transaction({
      Accounts: {
        [accountId]: (account) => ({
          ...account,
          followers_count: Math.max(0, account.followers_count - 1),
        }),
      },
      Relationships: {
        [accountId]: (relationship) => ({
          ...relationship,
          following: false,
        }),
      },
    });
  }

  async function follow(accountId: string, options: FollowOpts = {}) {
    if (!isLoggedIn) return;

    // Check if this is TrueMapleAds - block follow requests
    const response = await api.get(`/api/v1/accounts/${accountId}`);
    const parsed = accountSchema.safeParse(await response.json());

    if (!parsed.success || isFollowDisabled(parsed.data)) {
      console.warn("Follow requests are disabled for this account");
      return;
    }

    followEffect(accountId);

    try {
      const response = await api.post(
        `/api/v1/accounts/${accountId}/follow`,
        options,
      );
      const result = relationshipSchema.safeParse(await response.json());
      if (result.success) {
        dispatch(importEntities([result.data], Entities.RELATIONSHIPS));
      }
    } catch (e) {
      unfollowEffect(accountId);
    }
  }

  async function unfollow(accountId: string) {
    if (!isLoggedIn) return;

    // Check if this is TrueMapleAds - block unfollow requests
    const response = await api.get(`/api/v1/accounts/${accountId}`);
    const parsed = accountSchema.safeParse(await response.json());

    if (!parsed.success || isFollowDisabled(parsed.data)) {
      console.warn("Follow requests are disabled for this account");
      return;
    }

    unfollowEffect(accountId);

    try {
      await api.post(`/api/v1/accounts/${accountId}/unfollow`);
    } catch (e) {
      followEffect(accountId);
    }
  }

  return {
    follow,
    unfollow,
    followEffect,
    unfollowEffect,
  };
}

export { useFollow };
